import HeaderPortal from "components/portal/header-portal";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "ui/components/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "ui/components/select";
import { formatDateForLogOptions } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TimesheetSelect from "@/components/common-select";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form/index";
import { Input } from "ui/components/input";
import { Textarea } from "ui/components/textarea";
import { eventBus } from "utils/eventBus";
import { draftTimesheetLogSchema } from "@/modules/timesheet/draft/log/create/schema";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useFetch } from "@/hooks/useFetch";
import { useLockTimesheet } from "components/timesheet/lock/useLock";
import { useLogDraftTimesheetEntry } from "components/timesheet/draft/mutation";
import useRemoveSearchParam from "@/hooks/useRemoveSearchParams";
import { zodResolver } from "@hookform/resolvers/zod";
import { dateOptions } from "@/modules/timesheet/draft/log/create/utils";
import { fetchTags } from "../../api";

export default function DraftTimesheetLog({ command = false, onSuccess }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isNew, setIsNew] = useState(false);
    const [currentDate, setCurrentDate] = useState(null);
    const date = searchParams.get("date");
    const navigate = useNavigate();

    const logDraftTimesheetMutation = useLogDraftTimesheetEntry();
    
    const { data: tags } = useFetch({
        queryKey: ["timesheet", "tags"],
        queryFn: () => fetchTags(),
    });
    const form = useForm({
        resolver: zodResolver(draftTimesheetLogSchema),
        defaultValues: {
            date: "",
            logged_hours: "",
            description: "",
        },
    });

    const { canEdit } = useLockTimesheet({ date: currentDate });

    useEffect(() => {
        if (date) {
            form.setValue("date", date);
            setCurrentDate(date);
        }
    }, [date, form.getValues("date")]);

    useRemoveSearchParam("date", !!date);

    const handleClose = () => {
        navigate(-1);
    };

    if (!canEdit && date) {
        navigate(`/draft/${date}`);
    }

    const handleCreate = async (context) => {
        const values = context.values;
        const date = values.date;

        await logDraftTimesheetMutation.mutateAsync({ values });

        eventBus.emit("refresh", `draft_timesheet-${date}`);

        navigate(-1);

        return {
            date: date,
            tag: tag.id,
            logged_hours: "",
            description: "",
        };
    };

    return (
        <div className="w-full h-full p-6">
            <Form
                form={form}
                schema={draftTimesheetLogSchema}
                itemId={"+"}
                create={handleCreate}
                className="h-full"
            >
                {({ isCreating, createItem }) => {
                    useHotkeys(
                        "ctrl+s, cmd+s",
                        (event) => {
                            event.preventDefault();
                            if (!isCreating) {
                                setIsNew(false);
                                createItem().then(handleClose);
                            }
                        },
                        { preventDefault: true }
                    );

                    useHotkeys(
                        "ctrl+shift+s, cmd+shift+s",
                        (event) => {
                            event.preventDefault();
                            if (!isCreating) {
                                setIsNew(true);
                                createItem();
                            }
                        },
                        { preventDefault: true }
                    );
                    return (
                        <>
                            <div className="flex gap-6 md:flex-row flex-col w-full h-full">
                                <div className="space-y-4 w-80">
                                    <FormField
                                        control={form.control}
                                        name="date"
                                        render={({ field }) => {
                                            const dateExistsInOptions = dateOptions.some(
                                                (opt) => opt.value === field.value
                                            );
                                            const hasDate = field.value && field.value.trim() !== "";

                                            return (
                                                <FormItem>
                                                    <FormLabel>Date</FormLabel>
                                                    <FormControl>
                                                        {!hasDate || dateExistsInOptions ? (
                                                            <Select
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Select Date" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {dateOptions.map((opt) => (
                                                                        <SelectItem
                                                                            key={opt.offset}
                                                                            value={opt.value}
                                                                        >
                                                                            {opt.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        ) : (
                                                            <div className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                                                                <span className="text-muted-foreground">
                                                                    {field.value
                                                                        ? formatDateForLogOptions(field.value)
                                                                        : "No date selected"}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            );
                                        }}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="tag"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Tag</FormLabel>
                                                <FormControl>
                                                    <TimesheetSelect
                                                        value={field.value}
                                                        onValueChange={field.onChange}
                                                        items={tags}
                                                        groupLabel="Tag"
                                                        displayProperty="label"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="logged_hours"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Hours</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        placeholder="ex. 6.5"
                                                        {...field}
                                                        step="0.5"
                                                        min="0"
                                                        max="24"
                                                        onWheel={(e) => e.target.blur()}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value === "") {
                                                                field.onChange(e);
                                                                return;
                                                            }

                                                            if (value < 0 || (value * 10) % 5 !== 0) {
                                                                e.target.value = "";
                                                                return;
                                                            }
                                                            field.onChange(e);
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className="flex-1 h-full">
                                    <FormField
                                        control={form.control}
                                        name="description"
                                        render={({ field }) => (
                                            <FormItem className="flex-1 flex flex-col h-full">
                                                <FormLabel>Description</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="Write Description"
                                                        className="h-full flex-1"
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <div>
                                {!command ? (
                                    <HeaderPortal>
                                        <div className="space-x-2">
                                            <ButtonWithLoading
                                                isLoading={isCreating && isNew}
                                                onClick={() => {
                                                    setIsNew(true);
                                                    createItem();
                                                }}
                                                type="button"
                                            >
                                                Save and Create
                                            </ButtonWithLoading>
                                            <ButtonWithLoading
                                                variant="default"
                                                isLoading={isCreating && !isNew}
                                                onClick={() => {
                                                    setIsNew(false);
                                                    createItem().then(handleClose);
                                                }}
                                            >
                                                Save
                                            </ButtonWithLoading>
                                        </div>
                                    </HeaderPortal>
                                ) : (
                                    <div className="space-x-2 flex justify-end">
                                        <ButtonWithLoading
                                            isLoading={isCreating && isNew}
                                            onClick={() => {
                                                setIsNew(true);
                                                createItem();
                                            }}
                                            type="button"
                                        >
                                            Save and Create
                                        </ButtonWithLoading>
                                        <ButtonWithLoading
                                            variant="default"
                                            isLoading={isCreating && !isNew}
                                            onClick={() => {
                                                setIsNew(false);
                                                createItem().then(handleClose);
                                            }}
                                        >
                                            Save
                                        </ButtonWithLoading>
                                    </div>
                                )}
                            </div>
                        </>
                    );
                }}
            </Form>
        </div>
    );
}
